import { useCallback, useEffect, useState } from 'react';

export type Appearance = 'light' | 'dark' | 'system';

const prefersDark = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const getStoredAppearance = (): Appearance => {
    if (typeof window === 'undefined') {
        return 'system';
    }

    return (
        (window.localStorage.getItem('appearance') as Appearance | null) ??
        'system'
    );
};

const setCookie = (name: string, value: string, days = 365) => {
    if (typeof document === 'undefined') {
        return;
    }

    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
};

const applyTheme = (appearance: Appearance) => {
    if (typeof document === 'undefined') {
        return;
    }

    const isDark =
        appearance === 'dark' || (appearance === 'system' && prefersDark());

    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
};

const mediaQuery = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
};

const handleSystemThemeChange = () => {
    if (typeof window === 'undefined') {
        return;
    }

    const currentAppearance = localStorage.getItem('appearance') as Appearance;
    applyTheme(currentAppearance || 'system');
};

// Notify in-tree subscribers when the appearance changes from any caller of
// updateAppearance. This keeps every `useAppearance()` consumer in sync
// without forcing them to share React context.
type Listener = (next: Appearance) => void;
const listeners = new Set<Listener>();

const broadcastAppearance = (next: Appearance) => {
    for (const listener of listeners) {
        listener(next);
    }
};

export function initializeTheme() {
    if (typeof window === 'undefined') {
        return;
    }

    const savedAppearance = getStoredAppearance();

    applyTheme(savedAppearance);

    // Global system-theme listener — installed once for the page lifetime so
    // consumers can mount/unmount without dropping the subscription.
    mediaQuery()?.addEventListener('change', handleSystemThemeChange);
}

export function useAppearance() {
    const [appearance, setAppearance] = useState<Appearance>(() =>
        getStoredAppearance(),
    );

    const updateAppearance = useCallback((mode: Appearance) => {
        setAppearance(mode);
        broadcastAppearance(mode);

        // Store in localStorage for client-side persistence...
        localStorage.setItem('appearance', mode);

        // Store in cookie for SSR...
        setCookie('appearance', mode);

        applyTheme(mode);
    }, []);

    useEffect(() => {
        const listener: Listener = (next) => setAppearance(next);
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }, []);

    return { appearance, updateAppearance } as const;
}
